import { faEnvelope, faPhone } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import constants from "../constants/constants";
import { useEffect, useState } from "react";
import SEO, { pageSEO } from "../components/seo.component";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../functions/animations";
import { useUserParams } from "../hooks/useUserParams";

export default function Contact() {
  const [popup, setPopup] = useState(false);
  const {
    getCurrentAgentName,
    getCurrentAgentTitle,
    getCurrentAgentNumber,
    getCurrentAgentEmail,
    getCurrentBrokerage,
  } = useUserParams();
  const [formData, setFormData] = useState({
    Name: "",
    "Email Address": "",
    Message: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  async function submitForm(event) {
    event.preventDefault();

    const data = {
      ...formData,
      access_key: constants.EMAIL_ACCESS_KEY,
      subject: "New Contact Form Message",
    };

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(data),
      });

      const response = await res.json();

      if (response?.success) {
        console.log("Success", response);
        setPopup({
          subject: "Thanks for your message!",
          message: "We will get back to you as soon as possible",
        });
        // Clear form after successful submission
        setFormData({
          Name: "",
          "Email Address": "",
          Message: "",
        });
      } else {
        setPopup({
          subject: "We encountered an error in sending this message",
          message: "Please try again later",
        });
      }
    } catch (err) {
      console.log(err);
      setPopup({
        subject: "We encountered an error in sending this message",
        message: "Please try again later",
      });
    }
  }

  const seoConfig = pageSEO.contact();
  return (
    <>
      <SEO {...seoConfig} />

      <section className="bg-summit-muted/10 min-h-screen pt-36 pb-20">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="max-w-6xl mx-auto px-6">

          {/* Header */}
          <div className="text-center mb-16">
            <motion.p
              variants={fadeUp}
              className="uppercase tracking-[5px] text-summit-highlight text-sm font-medium">
              Get In Touch
            </motion.p>

            <motion.h1
              variants={fadeUp}
              className="font-heading text-5xl md:text-6xl text-summit-primary mt-4">
              Contact Us
            </motion.h1>

            <motion.div
              variants={fadeUp}
              className="w-20 h-1 bg-summit-highlight rounded-full mx-auto mt-6"></motion.div>

            <motion.p
              variants={fadeUp}
              className="max-w-2xl mx-auto mt-6 text-summit-muted leading-8 font-body">
              Whether you're buying, selling, or simply have questions, we're here
              to help. Send us a message and we'll get back to you as soon as
              possible.
            </motion.p>
          </div>

          {/* Main Content */}
          <motion.div
            variants={fadeUp}
            className="grid lg:grid-cols-[2fr_1fr] gap-10">

            {/* Contact Form */}
            <div className="bg-white rounded-3xl shadow-lg p-10">

              <h2 className="font-heading text-3xl text-summit-primary mb-8">
                Send a Message
              </h2>

              <form onSubmit={submitForm} className="space-y-6">

                <div>
                  <label className="block mb-2 text-sm uppercase tracking-wider text-slate-600">
                    Full Name
                  </label>

                  <input
                    name="Name"
                    value={formData.Name}
                    onChange={handleInputChange}
                    placeholder="John Smith"
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-5 py-4 outline-none transition focus:border-summit-highlight focus:bg-white"
                  />
                </div>

                <div>
                  <label className="block mb-2 text-sm uppercase tracking-wider text-slate-600">
                    Email Address
                  </label>

                  <input
                    name="Email Address"
                    value={formData["Email Address"]}
                    onChange={handleInputChange}
                    placeholder="john@email.com"
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-5 py-4 outline-none transition focus:border-summit-highlight focus:bg-white"
                  />
                </div>

                <div>
                  <label className="block mb-2 text-sm uppercase tracking-wider text-slate-600">
                    Message
                  </label>

                  <textarea
                    name="Message"
                    value={formData.Message}
                    onChange={handleInputChange}
                    placeholder="Tell us how we can help..."
                    rows={6}
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-5 py-4 outline-none transition focus:border-summit-highlight focus:bg-white"
                  />
                </div>

                <button
                  type="submit"
                  className="btn-luxury rounded-xl"
                >
                  Send Message
                </button>

              </form>
            </div>

            {/* Contact Card */}
            <div className="bg-white rounded-3xl shadow-lg p-10 h-fit">

              <span className="uppercase tracking-[3px] text-summit-highlight text-sm">
                Contact Information
              </span>

              <h2 className="font-heading text-3xl text-summit-primary mt-3">
                {getCurrentAgentName()}
              </h2>

              <p className="text-summit-muted italic mt-2">
                {getCurrentAgentTitle()}
                <br />
                {getCurrentBrokerage()}
              </p>

              <div className="mt-10 space-y-6">

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-summit-highlight/15 flex items-center justify-center">
                    <FontAwesomeIcon
                      icon={faPhone}
                      className="text-summit-highlight"
                    />
                  </div>

                  <div>
                    <p className="text-sm text-summit-muted">Phone</p>
                    <p className="font-semibold text-summit-primary">
                      {getCurrentAgentNumber()}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-summit-highlight/15 flex items-center justify-center">
                    <FontAwesomeIcon
                      icon={faEnvelope}
                      className="text-summit-highlight"
                    />
                  </div>

                  <div>
                    <p className="text-sm text-summit-muted">Email</p>
                    <p className="font-semibold text-summit-primary break-all">
                      {getCurrentAgentEmail()}
                    </p>
                  </div>
                </div>

              </div>

            </div>

          </motion.div>
        </motion.div>
      </section>

      {popup && (
        <>
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" />

          <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-white rounded-3xl shadow-2xl p-10 max-w-md w-[90%] text-center">
            <div className="w-16 h-16 rounded-full bg-summit-highlight/20 flex items-center justify-center mx-auto mb-6">
              ✓
            </div>

            <h3 className="font-heading text-3xl text-summit-primary">
              {popup.subject}
            </h3>

            <p className="text-summit-muted mt-4 leading-7">
              {popup.message}
            </p>

            <button
              onClick={() => setPopup(false)}
              className="mt-8 bg-summit-secondary hover:bg-summit-primary transition text-white px-8 py-3 rounded-xl"
            >
              Close
            </button>
          </div>
        </>
      )}
    </>
  );
}
